import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Phone, Mail, MapPin, CreditCard, Calendar } from "lucide-react";
import { StatusBadge } from "@/components/admin/shared";
import type { Order, OrderDelivery } from "@/lib/admin-api/orders";

// ---------------------------------------------------------------------------
// FCFA formatter — space separator, no decimals
// ---------------------------------------------------------------------------

function formatFCFA(amount: number): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(amount)
    .concat(" FCFA");
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface OrderSummaryProps {
  readonly order: Order;
  readonly delivery: OrderDelivery | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OrderSummary({ order, delivery }: OrderSummaryProps) {
  const { t } = useTranslation("admin");

  return (
    <div className="rounded-xl border border-stone-200 bg-white">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-100 px-5 py-4">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-lg font-semibold text-stone-900">
            {t("order_detail.title", { ref: order.public_ref })}
          </h2>
          <StatusBadge status={order.status} variant="order" />
        </div>
        <div className="flex items-center gap-1.5 text-xs text-stone-500">
          <Calendar className="h-3.5 w-3.5" />
          {t("order_detail.created_at")}{" "}
          {format(new Date(order.created_at), "dd/MM/yyyy 'à' HH:mm", {
            locale: fr,
          })}
        </div>
      </div>

      {/* Body — 2 col on desktop, stacked on mobile */}
      <div className="grid gap-6 p-5 md:grid-cols-2">
        {/* Left — customer info */}
        <div className="space-y-3">
          <InfoRow
            icon={<Phone className="h-4 w-4 text-stone-400" />}
            label={t("order_detail.customer")}
            value={order.customer_name}
          />
          <InfoRow
            icon={<Phone className="h-4 w-4 text-stone-400" />}
            label={t("order_detail.phone")}
            value={order.customer_phone}
          />
          {order.customer_email && (
            <InfoRow
              icon={<Mail className="h-4 w-4 text-stone-400" />}
              label={t("order_detail.email")}
              value={order.customer_email}
            />
          )}
          <InfoRow
            icon={<MapPin className="h-4 w-4 text-stone-400" />}
            label={t("order_detail.delivery_address")}
            value={order.delivery_address}
          />
          {order.region && (
            <InfoRow
              icon={<MapPin className="h-4 w-4 text-stone-400" />}
              label={t("order_detail.region")}
              value={order.region.name}
            />
          )}
          <InfoRow
            icon={<CreditCard className="h-4 w-4 text-stone-400" />}
            label={t("order_detail.payment_method")}
            value={order.payment_method}
          />
        </div>

        {/* Right — amounts */}
        <div className="space-y-2 rounded-lg bg-stone-50 p-4">
          <AmountRow
            label={t("order_detail.subtotal_ht")}
            value={formatFCFA(order.subtotal_ht)}
          />
          <AmountRow
            label={t("order_detail.tax")}
            value={formatFCFA(order.tax_amount)}
          />
          <AmountRow
            label={t("order_detail.delivery_fee")}
            value={formatFCFA(order.delivery_fee)}
          />
          <div className="border-t border-stone-200 pt-2">
            <AmountRow
              label={t("order_detail.total_ttc")}
              value={formatFCFA(order.total_ttc)}
              bold
            />
          </div>

          {/* Delivery info if available */}
          {delivery && (
            <div className="mt-4 space-y-2 border-t border-stone-200 pt-3">
              <p className="text-xs font-medium uppercase tracking-wider text-stone-500">
                {t("order_detail.delivery")}
              </p>
              {delivery.driver && (
                <p className="text-sm text-stone-700">
                  {t("order_detail.driver")}: {delivery.driver.full_name} ({delivery.driver.phone})
                </p>
              )}
              {delivery.method && (
                <p className="text-sm text-stone-700">
                  {delivery.method.name}
                </p>
              )}
              <StatusBadge status={delivery.status} variant="delivery" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function InfoRow({
  icon,
  label,
  value,
}: {
  readonly icon: React.ReactNode;
  readonly label: string;
  readonly value: string;
}) {
  return (
    <div className="flex items-start gap-2">
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-stone-500">{label}</p>
        <p className="text-sm font-medium text-stone-900">{value}</p>
      </div>
    </div>
  );
}

function AmountRow({
  label,
  value,
  bold = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly bold?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? "text-sm font-semibold text-stone-900" : "text-sm text-stone-600"}>
        {label}
      </span>
      <span className={bold ? "text-sm font-semibold text-stone-900" : "text-sm text-stone-700"}>
        {value}
      </span>
    </div>
  );
}
